import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, UserRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

export default function SharedEmployeeSwitcher({ collapsed }) {
  const { user, activeEmployeeId, activeEmployee, setActiveEmployeeId } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const employees = user?.shared_employee_profiles || [];

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user?.is_shared_account || employees.length === 0) return null;

  const selectEmployee = (employeeId) => {
    setActiveEmployeeId(employeeId);
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    setOpen(false);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex justify-center p-2 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
        title={activeEmployee?.name || 'Employee'}
      >
        <UserRound className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/80 hover:bg-white/10 transition-colors text-sm"
      >
        <UserRound className="w-5 h-5 text-white/50 flex-shrink-0" />
        <span className="truncate flex-1 text-left font-medium">
          {activeEmployee?.name || 'Select employee'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          {employees.map((employee) => (
            <button
              key={employee.id}
              type="button"
              onClick={() => selectEmployee(employee.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                String(employee.id) === String(activeEmployeeId)
                  ? 'text-emerald-700 font-medium'
                  : 'text-slate-700'
              }`}
            >
              <span className="truncate flex-1 text-left">{employee.name}</span>
              {String(employee.id) === String(activeEmployeeId) && (
                <Check className="w-4 h-4 text-emerald-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
